import type { CustomerIdentifier } from "./customerIdentity";

const API_BASE =
  import.meta.env.VITE_PUBLIC_SOCIAL_OCEAN_BASE_URL || "https://socialocean.co";
const PROXY_PREFIX = "/api/widget/rate-product";

export type PdpRatingEligibility =
  | { eligible: false }
  | {
      eligible: true;
      productId: number;
      title: string;
      questionId: number;
      shopifyOrderId: string;
      ratingCount: number;
    };

export async function fetchPdpRatingEligibility({
  shop,
  shopifyProductId,
  identifier,
}: {
  shop: string;
  shopifyProductId: number;
  identifier: CustomerIdentifier;
}): Promise<PdpRatingEligibility> {
  const url = new URL(`${API_BASE}${PROXY_PREFIX}/eligibility`);
  url.searchParams.set("shop", shop);
  url.searchParams.set("shopify_product_id", String(shopifyProductId));
  if (identifier.type === "id") {
    url.searchParams.set(
      "shopify_customer_id",
      String(identifier.shopify_customer_id),
    );
  } else {
    url.searchParams.set("buy_again_token", identifier.buy_again_token);
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    credentials: "omit",
  });
  if (!res.ok) return { eligible: false };

  const body = await res.json().catch(() => null);
  const data = body?.data ?? body ?? {};
  const product = data.product ?? {};
  if (product.id == null) return { eligible: false };

  return {
    eligible: true,
    productId: product.id,
    title: product.title,
    questionId: data.question_id,
    shopifyOrderId: String(data.shopify_order_id ?? ""),
    ratingCount:
      typeof product.ratings_count === "number" ? product.ratings_count : 0,
  };
}

export async function submitPdpRating({
  shop,
  questionId,
  shopifyOrderId,
  productId,
  rating,
  comment,
}: {
  shop: string;
  questionId: number;
  shopifyOrderId: string;
  productId: number;
  rating: 1 | 2 | 3 | 4 | 5;
  comment?: string | null;
}): Promise<void> {
  const trimmed = comment?.trim();
  const url = new URL(`${API_BASE}${PROXY_PREFIX}/submit`);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify({
      shop,
      question_id: questionId,
      shopify_order_id: shopifyOrderId,
      source: "product-page",
      responses: [
        {
          product_id: productId,
          response: trimmed ? trimmed : String(rating),
          rating,
        },
      ],
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to submit rating (${res.status})`);
  }
}
